import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, CheckCircle, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { api } from '../api';
import { useFetch } from '../hooks/useFetch';
import { useToast } from '../context/ToastContext';
import BookingCalendar from '../components/BookingCalendar';
import PageHero from '../components/PageHero';

const TIME_SLOTS = ['08:00','08:45','09:30','10:15','11:00','11:45','13:00','13:45','14:30','15:15','16:00','16:45','17:30'];
const STEPS = ['Välj Paket','Välj Tid','Dina Uppgifter','Bekräftelse'];

export default function Korlektioner() {
  const toast = useToast();
  const { data: packages } = useFetch(() => api.getPackages());
  const pkgs = packages || [{id:'pkg-5',lektioner:5,pris:3750,popular:false},{id:'pkg-10',lektioner:10,pris:7500,popular:true},{id:'pkg-15',lektioner:15,pris:11250,popular:false},{id:'pkg-20',lektioner:20,pris:15000,popular:false}];

  const [step, setStep] = useState(0);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [form, setForm] = useState({ name:'', email:'', phone:'', notes:'' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const canNext = [!!selectedPkg, !!(date && time), !!(form.name && form.email && form.phone)][step] ?? true;
  const pkg = pkgs.find(p => p.id === selectedPkg);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.createBooking({ packageId: selectedPkg, date, time, ...form });
      setConfirmation(r.booking);
      toast('Bokning bekräftad!', 'success');
    } catch (err) { toast(err.message, 'error'); }
    finally { setSubmitting(false); }
  };

  if (confirmation) return (
    <div>
      <PageHero title="Bokning" accent="Bekräftad" breadcrumbs={[{label:'Körlektioner',to:'/korlektioner'}]}/>
      <div className="max-w-lg mx-auto px-6 py-16 text-center">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="font-display font-black text-3xl text-gray-900 uppercase mb-3">Välkommen till Alfa!</h2>
        <p className="text-gray-500 mb-6">Bekräftelse skickas till <strong>{confirmation.email}</strong></p>
        <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 mb-6">
          {[['Bokningskod',confirmation.confirmationCode],['Paket',confirmation.packageName||`${confirmation.lektioner} lektioner`],['Datum',confirmation.date],['Tid',confirmation.time]].map(([k,v])=>(
            <div key={k} className="flex justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 text-sm"><span className="text-gray-500">{k}</span><strong className="text-gray-900">{v}</strong></div>
          ))}
        </div>
        <Link to="/" className="btn-red">Hem</Link>
      </div>
    </div>
  );

  return (
    <div>
      <PageHero title="Körlektioner &" accent="Priser" desc="Välj paket och boka online – vi hämtar dig vid dörren i Kista." breadcrumbs={[{label:'Körlektioner'}]}/>
      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_280px] gap-8 items-start">
        {/* Stepper */}
        <div className="lg:col-span-2 flex items-center gap-2 overflow-x-auto pb-2">
          {STEPS.map((label, i) => (
            <div key={i} className="flex items-center gap-2 shrink-0">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-black transition-colors ${i < step ? 'bg-green-500 text-white' : i === step ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-400'}`}>{i < step ? '✓' : i+1}</div>
              <span className={`text-xs font-bold uppercase tracking-wide whitespace-nowrap ${i === step ? 'text-red-600' : i < step ? 'text-green-600' : 'text-gray-400'}`}>{label}</span>
              {i < STEPS.length-1 && <div className="w-8 h-px bg-gray-200 mx-1 shrink-0"/>}
            </div>
          ))}
        </div>

        {/* Main panel */}
        <div>
          <form onSubmit={handleSubmit}>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-4">
              {step === 0 && (
                <>
                  <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-5 flex items-center gap-2"><Car size={18} className="text-red-600"/> Välj Körkortspaket</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {pkgs.map(p => (
                      <button key={p.id} type="button" onClick={() => setSelectedPkg(p.id)}
                        className={`relative text-center p-4 rounded-xl border-2 transition-all ${selectedPkg===p.id ? 'border-red-600 bg-red-50' : 'border-gray-100 hover:border-red-200 hover:bg-red-50/50'} ${p.popular ? 'border-red-300' : ''}`}>
                        {p.popular && <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[10px] font-black uppercase px-2 py-0.5 rounded-full whitespace-nowrap">Populärast</div>}
                        <div className="font-display font-black text-4xl text-red-600">{p.lektioner}</div>
                        <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">lek</div>
                        <div className="font-bold text-gray-900 text-sm">{p.pris.toLocaleString('sv-SE')} kr</div>
                        <div className="text-xs text-gray-400">750/lek</div>
                        {selectedPkg===p.id && <div className="absolute top-2 right-2 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center text-white text-[10px] font-black">✓</div>}
                      </button>
                    ))}
                  </div>
                  <div className="border-t border-gray-50 pt-4">
                    <h3 className="font-bold text-gray-700 text-sm mb-3">Vad ingår i varje lektion?</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {['45 min körtid','Certifierad instruktör','Modernt fordon','Anpassad undervisning','Teori-tips','Giltigt 12 mån'].map(f=>(
                        <div key={f} className="flex items-center gap-2 text-xs text-gray-500"><CheckCircle size={11} className="text-red-500 shrink-0"/>{f}</div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-5">Välj Datum & Tid</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Välj Datum</div>
                      <BookingCalendar selected={date} onChange={setDate}/>
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                        Välj Tid {date && <span className="text-red-500 normal-case font-normal">— {date}</span>}
                      </div>
                      {date ? (
                        <div className="grid grid-cols-3 gap-2">
                          {TIME_SLOTS.map(t => (
                            <button key={t} type="button" onClick={() => setTime(t)}
                              className={`py-2.5 rounded-lg text-xs font-mono font-semibold border transition-colors ${time===t ? 'bg-red-600 text-white border-red-600' : 'border-gray-200 text-gray-700 hover:border-red-300 hover:text-red-600'}`}>
                              {t}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-32 border-2 border-dashed border-gray-100 rounded-xl text-sm text-gray-400">
                          Välj datum för att se tider
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-5 flex items-center gap-2"><User size={18} className="text-red-600"/> Dina Uppgifter</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[['name','Namn','text','Förnamn Efternamn'],['email','E-post','email','din@email.se'],['phone','Telefon','tel','073-000 00 00']].map(([k,l,t,ph])=>(
                      <div key={k}>
                        <label className="label">{l} *</label>
                        <input type={t} required placeholder={ph} value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} className="input-field"/>
                      </div>
                    ))}
                    <div className="md:col-span-2">
                      <label className="label">Önskemål</label>
                      <textarea rows={3} placeholder="T.ex. specifika moment, hämtningsplats..." value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} className="input-field resize-none"/>
                    </div>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <h2 className="font-display font-black text-2xl text-gray-900 uppercase mb-5 flex items-center gap-2"><CheckCircle size={18} className="text-red-600"/> Bekräfta Bokning</h2>
                  <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100 mb-4">
                    {[['Paket',pkg?`${pkg.lektioner} körlektioner`:''],['Pris',pkg?`${pkg.pris.toLocaleString('sv-SE')} kr`:''],['Datum',date],['Tid',time],['Namn',form.name],['E-post',form.email],['Telefon',form.phone]].map(([k,v])=>(
                      <div key={k} className="flex justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 text-sm"><span className="text-gray-500">{k}</span><strong className="text-gray-900">{v}</strong></div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 bg-red-50 rounded-lg px-4 py-3">Genom att bekräfta accepterar du våra bokningsvillkor. Avbokning senast 24h i förväg.</p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3">
              {step > 0 && <button type="button" className="btn-red-outline" onClick={() => setStep(s => s-1)}>← Tillbaka</button>}
              {step < 3 ? (
                <button type="button" disabled={!canNext} onClick={() => setStep(s => s+1)} className="btn-red disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none">Nästa →</button>
              ) : (
                <button type="submit" disabled={submitting} className="btn-red disabled:opacity-40">
                  {submitting ? 'Bokar...' : <><CheckCircle size={14}/> Bekräfta</>}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Öppettider</h3>
            {[['Mån – Fre','08:00 – 18:00'],['Lördag','09:00 – 14:00'],['Söndag','Stängt']].map(([d,t])=>(
              <div key={d} className={`flex justify-between py-2 border-b border-gray-50 last:border-0 text-sm ${d==='Söndag'?'text-gray-300':''}`}><span className="text-gray-500">{d}</span><strong className={d==='Söndag'?'text-gray-300':'text-gray-900'}>{t}</strong></div>
            ))}
          </div>
          <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
            <h3 className="text-xs font-black uppercase tracking-widest text-red-500 mb-3">Ring oss</h3>
            <p className="text-sm text-red-700 mb-3">Hjälp att välja rätt paket?</p>
            <a href="tel:+46737547405" className="btn-red text-xs w-full justify-center mb-2"><Phone size={12}/> Abubakar</a>
            <a href="tel:+46704815743" className="btn-red text-xs w-full justify-center"><Phone size={12}/> Ahmed</a>
          </div>
          {pkg && (
            <div className="bg-white border border-gray-100 rounded-2xl p-5 text-center">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3">Valt Paket</h3>
              <div className="font-display font-black text-5xl text-red-600">{pkg.lektioner}</div>
              <div className="text-xs text-gray-400 uppercase tracking-widest mb-2">lektioner</div>
              <div className="font-display font-black text-2xl text-gray-900">{pkg.pris.toLocaleString('sv-SE')} kr</div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
